import type { IInsightSettings, IInsightSettingsJson, InsightKind, InsightTone } from '@platform/core/types';

/** Відомі значення union-полів: Record над union змушує компілятор оновити перелік разом із типом. */
const KINDS: Record<InsightKind, true> = {
  fieldMatrix: true, coverageMatrix: true, membershipMatrix: true, linksTable: true, linksMatrix: true,
  groupedByPath: true, teamLoad: true, hemicycle: true, library: true,
};
const MODES: Record<NonNullable<IInsightSettings['mode']>, true> = { heat: true, chips: true };
const TONES: Record<InsightTone, true> = { good: true, mixed: true, warn: true, bad: true, neutral: true };

/** Перевірка одного параметра: звужене значення або undefined (параметр відкидається, компонент бере типове значення). */
type Check = (value: unknown) => unknown;

const known = (table: object, value: unknown): value is string => typeof value === 'string' && Object.prototype.hasOwnProperty.call(table, value);
const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const str: Check = (v) => (typeof v === 'string' ? v : undefined);
const num: Check = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const bool: Check = (v) => (typeof v === 'boolean' ? v : undefined);
const strList: Check = (v) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined);
const kind: Check = (v) => (known(KINDS, v) ? v : undefined);
const mode: Check = (v) => (known(MODES, v) ? v : undefined);
const tones: Check = (v) => {
  if (!isObject(v)) return undefined;
  const valid = Object.entries(v).filter((entry) => known(TONES, entry[1]));
  return valid.length ? Object.fromEntries(valid) : undefined;
};
const hot: Check = (v) => (isObject(v) && typeof v.row === 'string' && typeof v.col === 'string' ? { row: v.row, col: v.col } : undefined);
/** Джерело осіб: потрібні непорожні рядки recordType і person; meta — рядок, statuses — рядки списку (решта відкидається). */
const peopleSource: Check = (v) => {
  if (!isObject(v) || typeof v.recordType !== 'string' || !v.recordType || typeof v.person !== 'string' || !v.person) return undefined;
  const statuses = strList(v.statuses);
  return { recordType: v.recordType, person: v.person, ...(typeof v.meta === 'string' && v.meta ? { meta: v.meta } : {}), ...(statuses ? { statuses } : {}) };
};

/** Перевірка на кожен ключ IInsightSettings: Record над keyof змушує додати перевірку разом з новим параметром. */
const CHECKS: Record<keyof IInsightSettings, Check> = {
  kind, recordType: str, statuses: strList, rows: str, cols: str, mode, reverseRows: bool, toneBy: str, tones, hot, breakdown: strList,
  rowBy: str, colBy: str, markBy: str, rowMeta: str, peopleSource, by: str, alt: str, person: str, seats: num, tooltip: strList, groupBy: str,
  assignee: str, estimate: str, remaining: str, start: str, due: str, finish: str, capacityPerDay: num, body: str,
};

/**
 * Звужує JSON-параметри аналітики (файл подання пакета — в assembleViewSet, будь-яке визначення, зокрема з сервера, — в insightSettingsOf)
 * до IInsightSettings за типом кожного параметра: `kind` — лише вид рушія (InsightKind), `mode` — 'heat' | 'chips', з `tones` лишаються
 * записи з відомим тоном, рядкові параметри — рядки, списки — масиви рядків (інші елементи відкидаються), числа — скінченні числа,
 * `hot` — { row, col } з рядків, `peopleSource` — { recordType, person, meta?, statuses? } з рядків. Значення не того типу відкидається, тож компонент рушія бере своє типове значення замість падіння.
 * Ключі, яких IInsightSettings не знає, передаються як є (сумісність із новішими визначеннями). Чиста функція.
 */
export function narrowInsightSettings(json: IInsightSettingsJson): IInsightSettings {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(isObject(json) ? json : {})) {
    const check = (CHECKS as Record<string, Check | undefined>)[key];
    if (!check) {
      out[key] = value;
      continue;
    }
    const narrowed = check(value);
    if (narrowed !== undefined) out[key] = narrowed;
  }
  return out as IInsightSettings;
}
